import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const CACHE_PREFIX = 'i18n_admin_';

/**
 * Loads admin UI strings from the backend so a language can be added/edited without a deploy.
 * Fallback chain keeps the app usable offline / if the API is down:
 *   backend  →  last-good cached response (localStorage)  →  the file bundled with this build.
 * The backend serves the admin bundle already nested, matching ngx-translate's lookup.
 */
@Injectable()
export class BackendTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<TranslationObject> {
    const cacheKey = `${CACHE_PREFIX}${lang}`;
    return this.http
      .get<TranslationObject>(`${environment.apiUrl}/locales/${lang}/translations?bundle=admin`)
      .pipe(
        tap(translations => this.writeCache(cacheKey, translations)),
        catchError(() => this.fallback(lang, cacheKey)),
      );
  }

  private fallback(lang: string, cacheKey: string): Observable<TranslationObject> {
    const cached = this.readCache(cacheKey);
    if (cached) {
      return of(cached);
    }
    // Last resort: the translation file bundled with this build.
    return this.http
      .get<TranslationObject>(`./i18n/${lang}.json`)
      .pipe(catchError(() => of({} as TranslationObject)));
  }

  private writeCache(key: string, value: TranslationObject): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full/unavailable — the network path still works this session.
    }
  }

  private readCache(key: string): TranslationObject | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as TranslationObject) : null;
    } catch {
      return null;
    }
  }
}
